import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, LoaderCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CameraCapture } from "@/components/CameraCapture";
import { BrandWordmark } from "@/components/Brand";
import { LanguageToggle } from "@/components/LanguageToggle";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { pdpaClauseEn, pdpaClauseBm } from "@/content/legal";

/* PDPA 2010 biometric consent: bilingual clause + explicit tick + a live
   acceptance selfie (camera only). Punching and enrolment stay blocked until
   this is on record. */
export default function PdpaConsentPage() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const host = window.location.hostname;
  const [agreed, setAgreed] = useState(false);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("photo", blob, "pdpa-acceptance.jpg");
    try {
      await api.post("/auth/pdpa-accept", fd);
      await refreshUser();
      setDone(true);
      toast.success(t("pdpaRecorded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
      setBlob(null);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6">
        <CheckCircle2 className="h-16 w-16 text-emerald-600" />
        <h1 data-testid="pdpa-done-title" className="text-2xl font-semibold tracking-tight">{t("pdpaRecorded")}</h1>
        <Button data-testid="pdpa-done-button" onClick={() => navigate("/staff", { replace: true })} className="h-12 px-8">
          {t("done")}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
        <BrandWordmark className="text-base" markClassName="h-7 w-7" />
        <LanguageToggle />
      </header>
      <main className="mx-auto max-w-3xl px-6 pb-24" data-testid="pdpa-consent-page">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">{t("pdpaTitle")}</h1>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {user?.fullName} · {t("pdpaIntro")}
        </p>

        <div data-testid="pdpa-clause" className="mt-6 max-h-[46vh] space-y-6 overflow-y-auto rounded-xl border border-border bg-card p-5">
          {pdpaClauseEn(host).map((s) => (
            <section key={s.h}>
              <h2 className="text-sm font-semibold">{s.h}</h2>
              <p className="mt-1 text-sm leading-relaxed text-foreground/85">{s.p}</p>
            </section>
          ))}
          <hr className="border-border" />
          {pdpaClauseBm(host).map((s) => (
            <section key={s.h}>
              <h2 className="text-sm font-semibold">{s.h}</h2>
              <p className="mt-1 text-sm leading-relaxed text-foreground/85">{s.p}</p>
            </section>
          ))}
        </div>

        <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-card p-4">
          <Checkbox
            checked={agreed}
            onCheckedChange={(v) => setAgreed(Boolean(v))}
            data-testid="pdpa-agree-checkbox"
            className="mt-0.5"
          />
          <span className="text-sm leading-6">{t("pdpaAgreeLabel")}</span>
        </label>

        {agreed && (
          <div className="mt-5">
            <p className="mb-2 text-sm font-medium">{t("pdpaSelfieNote")}</p>
            <div className="rk-camera-screen flex h-[420px] flex-col overflow-hidden rounded-xl">
              <CameraCapture
                onCapture={setBlob}
                capturedBlob={blob}
                onRetake={() => setBlob(null)}
                disabled={busy}
              />
            </div>
            {blob && (
              <Button
                data-testid="pdpa-submit-button"
                onClick={submit}
                disabled={busy}
                className="mt-4 h-14 w-full text-base font-semibold"
              >
                {busy ? (<><LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> {t("submitting")}</>) : t("pdpaAcceptButton")}
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
